
import { Vue, Component, Prop } from "vue-property-decorator";
import IconButton from "@/components/UI/IconButton.vue";
import VariableRepeaterItem from "@/oxygen/variables/VariableRepeaterItem.vue";
import draggable from "vuedraggable";
import Icon from "@/components/UI/Icon.vue";
import { jsonStringifyOrUndefined } from "@/util/json-stringify-or-undefined";
import slugify from "slugify";
import { triggerDownload } from "@/util/trigger-download";
import { DEFAULT_NEW_COLLECTION } from "@/oxygen/classes/lib/classes";
import defensiveCopy from "@/util/defensive-copy";

@Component({
  components: {
    Icon,
    draggable,
    VariableRepeaterItem,
    IconButton
  }
})
export default class OxyCollections extends Vue {
  @Prop()
  value!: { collection: string }[];

  @Prop()
  collections!: string[];

  @Prop()
  resourceName!: string;

  toggledCollections: string[] = [];

  dragOptions = {
    animation: 200,
    handle: ".oxy-collection__handle",
    ghostClass: "breakdance-inline-repeater-item--ghost",
    forceFallback: true
  };

  isToggled(collection: string) {
    return this.toggledCollections.includes(collection);
  }

  toggleCollection(collection: string) {
    if (this.toggledCollections.includes(collection)) {
      this.toggledCollections = this.toggledCollections.filter(
        item => item !== collection
      );
    } else {
      this.toggledCollections.push(collection);
    }
  }

  handleDrag(collections: string[]) {
    const sortedItems = defensiveCopy(this.value).sort((a, b) => {
      const indexA = collections.indexOf(a.collection);
      const indexB = collections.indexOf(b.collection);

      // If collections are the same, maintain relative order
      if (indexA === indexB) {
        return 0;
      }

      // Otherwise, sort by the collection index
      return indexA - indexB;
    });

    this.$emit("update-collections", collections);
    this.$emit("input", sortedItems);
  }

  setCollectionName(event: Event, index: number) {
    const target = event.target as HTMLInputElement;
    const newName = target.value;
    const oldName = this.collections[index];
    const collections = this.collections;

    collections[index] = newName;

    const items = this.value.map(item => {
      if (item.collection === oldName) {
        item.collection = newName;
      }
      return item;
    });

    this.$emit("update-collections", collections);
    this.$emit("input", items);
  }

  deleteCollection(collectionToDelete: string) {
    const confirmDelete = confirm(
      `Are you sure you want to delete the collection "${collectionToDelete}"?`
    );

    if (!confirmDelete) {
      return;
    }

    const items = this.value.filter(
      item => item.collection !== collectionToDelete
    );
    const collections = this.collections.filter(
      collection => collection !== collectionToDelete
    );

    this.$emit("update-collections", collections);
    this.$emit("input", items);
  }

  exportCollection(collection: string) {
    const items = this.value.filter(item => item.collection === collection);

    const itemsJson = jsonStringifyOrUndefined(items);
    const slug = slugify(collection, { lower: true });
    triggerDownload(itemsJson || "", `${slug}-${this.resourceName}.json`);
  }

  addCollection() {
    const newCollection = DEFAULT_NEW_COLLECTION;
    const existingCollections = this.collections;

    let count = 1;
    while (existingCollections.includes(`${newCollection} ${count}`)) {
      count++;
    }

    const finalCollectionName = `${newCollection} ${count}`;

    this.$emit("update-collections", [
      ...existingCollections,
      finalCollectionName
    ]);
  }
}
